import type { ModelViewerMaterialPrimitive, ModelViewerMaterialPrimitiveType } from "@centricsoftware/model-viewer";
import { C8Node } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ComponentItem } from "designSystem/types/component-item-types";
import { action, computed, makeObservable, observable } from "mobx";
import CanvasStore from "./canvasStore";

type MaterialSide = "front" | "back";

export const DEFAULT_MATERIAL_GEOMETRY = "MaterialPrimitiveFabric";

export default class MaterialSettingsStore extends ReactionStore {
	/** Stores document root Node */
	rootNode: C8Node;

	/** Stores document revision */
	revisionNode: C8Node;

	/** store for canvas state */
	canvasStore: CanvasStore;

	/** Entry point for ModelViewer active material face - default is front */
	@observable activeMaterialFace: MaterialSide = "front";

	/** Entry point for ModelViewer Material Primitives */
	@observable.ref modelViewerMaterialPrimitives: ModelViewerMaterialPrimitive[];

	/** Active material geometry */
	@observable.ref activeMaterialGeometry: ModelViewerMaterialPrimitiveType = DEFAULT_MATERIAL_GEOMETRY;

	/** Entry point for ModelViewer set material geometry */
	@observable.ref modelViewerSetMaterialGeometry: (geometry: string) => void;

	/** Entry point for ModelViewer setActiveMaterialFace */
	@observable.ref modelViewerSetActiveMaterialFace: (face: MaterialSide) => void;

	/** toggle Material side */
	@action toggleMaterialSide = (event, value: MaterialSide): void => {
		if (!value) {
			return;
		}
		
		this.activeMaterialFace = value;
		this.modelViewerSetActiveMaterialFace?.(value);
	};

	/** Determine if mesh is a u3m fabric */
	get isU3MFabric(): boolean {
		const { meshData } = this.canvasStore;
		return !!meshData?.userData?.isU3MFabric;
	}

	/** Compute wheter or not a material has a back side from the meshData */
	@computed get hasBackSide(): boolean {
		const { meshData } = this.canvasStore;
		const firstChild = meshData?.children[0] as THREE.Mesh;
		const materials = firstChild?.material as THREE.Material[];
		
		return materials?.length === 2 && this.isU3MFabric;
	}

	/** determine if primitive selector should show */
	@computed get showPrimitiveSelector(): boolean {
		const { modelViewerMaterialPrimitives, materialGeometries } = this;
		return materialGeometries && modelViewerMaterialPrimitives.length > 1;
	}

	/** return the different types of material geometries */
	@computed get materialGeometries(): ComponentItem[] | undefined {
		const { modelViewerMaterialPrimitives } = this;
		return modelViewerMaterialPrimitives?.map((obj) => {
			return { 
				id: obj.name,
				res: obj.name,
			};
		});
	}

	/** set the material geometry */
	@action setMaterialGeometry = (item: ComponentItem): void => {
		const { id } = item as { id: ModelViewerMaterialPrimitiveType };
		this.modelViewerSetMaterialGeometry?.(id);
		this.setActiveMaterialGeometry(id);
	};

	/** set the activeMaterial geometry for u3m primitive type */
	@action setActiveMaterialGeometry = (geometry: ModelViewerMaterialPrimitiveType): void => {
		this.activeMaterialGeometry = geometry;
	};

	/** set the material primitives provided by ModelViewer */
	@action setMaterialPrimitives = (primitives: ModelViewerMaterialPrimitive[]): void => {
		this.modelViewerMaterialPrimitives = primitives;
	};

	constructor(revisionNode: C8Node, canvasStore: CanvasStore) {
		super();
		makeObservable(this);

		this.revisionNode = revisionNode;
		this.canvasStore = canvasStore;
	}
}